import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { SectionLabel } from "@/components/shared/SectionLabel";
import { Chip } from "@/components/shared/Chip";

interface Role {
  period: string;
  title: string;
  company: string;
  description: string;
  bullets?: string[];
  aiTools: string[];
}

const ROLES: Role[] = [
  {
    period: "2025 — Present",
    title: "Lead Product Designer and AI Builder",
    company: "HomeLight",
    description:
      "Lead design across our 4 product lines — Growth, Agent Referrals, Buy Before You Sell, and HomeLight Offers — covering design strategy, usability research, prototyping, and shipping code to production.",
    bullets: [
      "Accelerated lead growth by driving a ~5% lift in quiz-to-lead conversion on a high-traffic flow (~1,700 daily users) through usability testing and research.",
      "Led UI refactor across company websites. Redesigning the interface, shipping pixel-perfect updates and animations to production.",
      "Contributed features in a POC portal for real estate agents that generated 96 signups in its first week.",
      "Auditing and improving front-end experiences directly in code using Figma MCP and Cursor.",
    ],
    aiTools: ["Figma MCP", "Code Connect", "Cursor", "Claude", "Vercel"],
  },
  {
    period: "2023 — 2025",
    title: "Senior Product Designer — Buy Before You Sell Portal",
    company: "HomeLight",
    description:
      "Led end-to-end design of the Buy Before You Sell loan portal and loan pre-qualification form as the sole designer. Ran discovery research with Loan Officers to surface communication and submission gaps, shaping a two-year product roadmap. Designed the full Loan Officer journey — sign up, onboarding, team accounts, pre-qualification, review & approvals, eSigning, home visit scheduling, and document uploads.",
    bullets: [
      "Loan pre-qualification intake conversion grew from 11.3% to 45.7%",
      "Loan contracts increased 70%",
    ],
    aiTools: ["Figma", "User Testing", "Maze", "Hotjar"],
  },
  {
    period: "2022 — 2023",
    title: "Product Designer — Agent Web and Mobile app Tools",
    company: "HomeLight",
    description:
      "Designed user flows and UI improvements for agent-facing web and mobile app tools used during real estate transactions, while maintaining the design system.",
    aiTools: ["Figma", "User Testing"],
  },
  {
    period: "2017 — 2022",
    title: "UX/UI Designer",
    company: "Wizeline",
    description:
      "Designed digital products with cross-functional teams across fintech, digital media, retail, and insurance.",
    aiTools: ["Figma", "Sketch", "Invision"],
  },
  {
    period: "2016",
    title: "Visual Designer",
    company: "Ideando Diseño",
    description: "",
    bullets: [
      "Designed branding, illustration, web, and packaging projects for multiple clients.",
      "Managed creative production from concept to delivery.",
    ],
    aiTools: ["Adobe Suite"],
  },
];

const AI_TOOLS = [
  { name: "Claude / Anthropic", level: 95 },
  { name: "Cursor", level: 90 },
  { name: "Figma MCP", level: 88 },
  { name: "Vercel", level: 85 },
  { name: "Maze", level: 80 },
  { name: "Hotjar", level: 80 },
];

const DESIGN_SKILLS = [
  { name: "Systems Design", level: 95 },
  { name: "User Research", level: 88 },
  { name: "Prototyping", level: 92 },
  { name: "Visual Design", level: 85 },
  { name: "Interaction Design", level: 90 },
];

const SPECIALTIES = [
  "Adoption of AI tools",
  "Prototyping",
  "User Research",
  "Zero-handoff shipping",
  "Design systems",
  "Conversion optimization",
  "Cross-functional collaboration",
  "Research synthesis",
];

function SkillBar({ name, level }: { name: string; level: number }) {
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-xs font-[DM_Sans] text-[#1A1A1A]">{name}</span>
        <span className="text-xs font-[DM_Sans] text-[#888888]">{level}%</span>
      </div>
      <div className="h-1.5 bg-[#E8E2D9] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#1D3FE8] rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${level}%` }}
        />
      </div>
    </div>
  );
}

/**
 * CV section: 2-column layout with timeline (left) and sidebar (right).
 */
export function CV() {
  return (
    <section id="cv" className="py-24 px-6 bg-white border-t border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto">
        <AnimatedSection>
          <SectionLabel>Experience</SectionLabel>
          <div className="flex items-end justify-between mb-16 flex-wrap gap-4">
            <h2 className="font-[Bricolage_Grotesque] font-extrabold tracking-[-0.04em] text-[clamp(1.75rem,4vw,3rem)] text-[#1A1A1A]">
              What I've been<br />
              <span className="text-[#1D3FE8]">working on.</span>
            </h2>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-16">
          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-px bg-[#E8E2D9]" />
            <div className="flex flex-col gap-12">
              {ROLES.map((role, i) => (
                <AnimatedSection key={role.period} delay={i * 80}>
                  <div className="pl-8 relative">
                    {/* Timeline dot */}
                    <div className="absolute left-[-4px] top-1.5 w-2 h-2 rounded-full bg-[#1D3FE8]" />

                    <p className="text-xs font-medium font-[DM_Sans] text-[#888888] mb-1">
                      {role.period}
                    </p>
                    <h3 className="font-[Bricolage_Grotesque] font-bold text-lg text-[#1A1A1A] tracking-tight">
                      {role.title}
                    </h3>
                    <p className="text-sm text-[#1D3FE8] font-medium font-[DM_Sans] mb-3">
                      {role.company}
                    </p>
                    {role.description && (
                      <p className="text-sm text-[#888888] font-[DM_Sans] leading-relaxed mb-4">
                        {role.description}
                      </p>
                    )}
                    {role.bullets && (
                      <ul className="mb-4 space-y-2">
                        {role.bullets.map((b, i) => (
                          <li key={i} className="flex gap-2 text-sm text-[#888888] font-[DM_Sans] leading-relaxed">
                            <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[#888888]" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    {role.aiTools.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {role.aiTools.map((tool) => (
                          <Chip key={tool} label={tool} variant="default" />
                        ))}
                      </div>
                    )}
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <AnimatedSection delay={200}>
            <div className="flex flex-col gap-8">
              {/* Specialties */}
              <div className="bg-[#F7F8FA] rounded-2xl p-6">
                <p className="text-xs font-medium uppercase tracking-[0.07em] font-[DM_Sans] text-[#888888] mb-4">
                  Specialties
                </p>
                <div className="flex flex-col gap-2">
                  {SPECIALTIES.map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1D3FE8] flex-shrink-0" />
                      <span className="text-sm text-[#1A1A1A] font-[DM_Sans]">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
